// src/components/dialogs/AnexarUnificadoDialog.js
import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import './AnexarUnificadoDialog.css';

// ── Tipos de ficheiro ──────────────────────────────────────────────────────────
const MIME_OCR = [
  'application/pdf',
  'image/jpeg', 'image/jpg', 'image/png',
  'image/tiff', 'image/gif', 'image/bmp', 'image/webp',
];

// Imagens para OCR (Document AI) — PDF tratado à parte com fallback local
const EXT_OCR = ['.jpg', '.jpeg', '.png', '.tiff', '.tif', '.gif', '.bmp', '.webp'];

// Extensões para extracção local no Node (sem Document AI)
const EXT_LOCAL = ['.docx', '.txt'];

const MIME_AUDIO = [
  'audio/mpeg', 'audio/mp3',
  'audio/wav', 'audio/wave', 'audio/x-wav',
  'audio/flac', 'audio/x-flac',
  'audio/ogg',
  'audio/webm', 'video/webm',
];
const EXT_AUDIO = ['.mp3', '.wav', '.flac', '.ogg', '.webm'];

const EXT_TODOS = [...EXT_OCR, '.pdf', ...EXT_LOCAL, ...EXT_AUDIO];
const POLLING_INTERVAL = 6000;

/**
 * Detecta o tipo de processamento necessário para o ficheiro:
 *   'audio'     → Speech-to-Text pipeline
 *   'local'     → TextExtractor no Node (docx / txt)
 *   'pdf-local' → Tentar extracção local; fallback para OCR se PDF com imagens
 *   'ocr'       → Document AI pipeline (imagens)
 *   null        → não suportado
 */
function detectarTipoFicheiro(file) {
  if (MIME_AUDIO.includes(file.type)) return 'audio';
  const ext = '.' + file.name.split('.').pop().toLowerCase();
  if (EXT_AUDIO.includes(ext))  return 'audio';
  if (EXT_LOCAL.includes(ext))  return 'local';
  if (ext === '.pdf')            return 'pdf-local';
  if (MIME_OCR.includes(file.type) || EXT_OCR.includes(ext)) return 'ocr';
  return null;
}

function formatarTamanho(bytes) {
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

/**
 * Diálogo unificado de anexo: suporta upload novo com transcrição OCR/áudio/local
 * e selecção de documentos existentes (Meus Documentos).
 *
 * Props:
 *   onClose  — () => void
 *   onAnexar — (anexos: [{nome: string, conteudo: string}]) => void
 *   codCli   — number | null   (opcional, para categorizar uploads)
 *   titulo   — string          (opcional)
 *   multiple — boolean         (default true; permite multi-selecção em Meus Documentos)
 */
const AnexarUnificadoDialog = ({ onClose, onAnexar, codCli = null, titulo, multiple = true }) => {
  const [activeTab, setActiveTab] = useState('ficheiro');

  // ── Tab "Novo Ficheiro" ──────────────────────────────────────────────────────
  // Cada item: { id, file, tipo, status, progresso, texto, erro }
  // status: 'pending' | 'uploading' | 'processing' | 'done' | 'error'
  const [ficheiros, setFicheiros]     = useState([]);
  const [processando, setProcessando] = useState(false);
  const [erroGlobal, setErroGlobal]   = useState('');
  const [isDragging, setIsDragging]   = useState(false);
  const fileInputRef = useRef(null);
  const pollingRefs  = useRef(new Map()); // id → intervalId

  // ── Tab "Meus Documentos" ────────────────────────────────────────────────────
  const [documentos, setDocumentos]         = useState([]);
  const [loadingDocs, setLoadingDocs]       = useState(false);
  const [erroDocs, setErroDocs]             = useState('');
  const [search, setSearch]                 = useState('');
  const [page, setPage]                     = useState(1);
  const [hasMore, setHasMore]               = useState(true);
  const [selectedIds, setSelectedIds]       = useState([]);
  const [confirmingDocs, setConfirmingDocs] = useState(false);

  // ── Cleanup polling ao desmontar ─────────────────────────────────────────────
  useEffect(() => {
    const refs = pollingRefs.current;
    return () => { refs.forEach(id => clearInterval(id)); };
  }, []);

  // ── Carregar documentos ao mudar de aba / pesquisa / página ─────────────────
  const fetchDocumentos = useCallback(async () => {
    try {
      setLoadingDocs(true);
      setErroDocs('');
      const res = await axios.get('/api/attachments/documentos/list', {
        params: { search, page, limit: 10 },
      });
      if (res.data.success) {
        if (page === 1) {
          setDocumentos(res.data.data);
        } else {
          setDocumentos(prev => [...prev, ...res.data.data]);
        }
        setHasMore(res.data.pagination.hasMore);
      }
    } catch (e) {
      setErroDocs(e.response?.data?.message || 'Erro ao carregar documentos');
    } finally {
      setLoadingDocs(false);
    }
  }, [search, page]);

  useEffect(() => {
    if (activeTab === 'documentos') fetchDocumentos();
  }, [activeTab, fetchDocumentos]);

  // ── Helper para actualizar um item da lista ──────────────────────────────────
  const updateFicheiro = useCallback((id, patch) => {
    setFicheiros(prev => prev.map(f => f.id === id ? { ...f, ...patch } : f));
  }, []);

  // ── Auto-finalização quando todos os ficheiros terminam ──────────────────────
  useEffect(() => {
    if (!processando || !ficheiros.length) return;
    const allTerminal = ficheiros.every(f => f.status === 'done' || f.status === 'error');
    if (!allTerminal) return;

    const sucessos = ficheiros
      .filter(f => f.status === 'done' && f.texto)
      .map(f => ({ nome: f.file.name, conteudo: f.texto }));

    if (sucessos.length > 0) {
      onAnexar(sucessos);
      onClose();
    } else {
      setErroGlobal('Nenhum ficheiro processado com sucesso. Verifique os erros acima.');
      setProcessando(false);
    }
  }, [ficheiros, processando, onAnexar, onClose]);

  // ── Drag-and-drop ─────────────────────────────────────────────────────────────
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!processando) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // só sai se o cursor saiu mesmo do elemento (não de um filho)
    if (!e.currentTarget.contains(e.relatedTarget)) setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (processando) return;
    const dropped = Array.from(e.dataTransfer.files || []);
    if (!dropped.length) return;
    const novos = [];
    const erros = [];
    for (const file of dropped) {
      const tipo = detectarTipoFicheiro(file);
      if (!tipo) { erros.push(`${file.name}: tipo não suportado`); continue; }
      if (file.size > 500 * 1024 * 1024) { erros.push(`${file.name}: demasiado grande (máx. 500MB)`); continue; }
      novos.push({ id: crypto.randomUUID(), file, tipo, status: 'pending', progresso: 0, texto: null, erro: null });
    }
    setErroGlobal(erros.length ? erros.join(' | ') : '');
    setFicheiros(prev => [...prev, ...novos]);
  };

  // ── Selecção de ficheiros (múltipla) ─────────────────────────────────────────
  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) return;
    const novos = [];
    const erros = [];
    for (const file of selected) {
      const tipo = detectarTipoFicheiro(file);
      if (!tipo) { erros.push(`${file.name}: tipo não suportado`); continue; }
      if (file.size > 500 * 1024 * 1024) { erros.push(`${file.name}: demasiado grande (máx. 500MB)`); continue; }
      novos.push({ id: crypto.randomUUID(), file, tipo, status: 'pending', progresso: 0, texto: null, erro: null });
    }
    setErroGlobal(erros.length ? erros.join(' | ') : '');
    setFicheiros(prev => [...prev, ...novos]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── Remover ficheiro da lista ─────────────────────────────────────────────────
  const handleRemoverFicheiro = (id) => {
    if (pollingRefs.current.has(id)) {
      clearInterval(pollingRefs.current.get(id));
      pollingRefs.current.delete(id);
    }
    setFicheiros(prev => prev.filter(f => f.id !== id));
  };

  // ── Extracção local (docx / txt) ─────────────────────────────────────────────
  const processarLocal = async (item) => {
    updateFicheiro(item.id, { status: 'uploading', progresso: 0 });
    try {
      const formData = new FormData();
      formData.append('file', item.file);
      if (codCli) formData.append('cod_cli', String(codCli));
      formData.append('tipo_documento', 'digitalizado_ocr');

      const res = await axios.post('/api/ocr/extract-local', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (ev) => {
          if (ev.lengthComputable)
            updateFicheiro(item.id, { progresso: Math.round((ev.loaded / ev.total) * 100) });
        },
      });

      if (res.data.success) {
        updateFicheiro(item.id, { status: 'done', progresso: 100, texto: res.data.data.textoExtraido });
      } else {
        throw new Error(res.data.message || 'Erro desconhecido na extracção');
      }
    } catch (e) {
      console.error('processarLocal error:', e);
      updateFicheiro(item.id, { status: 'error', erro: e.response?.data?.message || e.message });
    }
  };

  // ── PDF local com fallback para OCR se PDF com imagens ───────────────────────
  const processarAsync = useCallback(async (item) => {
    const endpoint = item.tipo === 'audio' ? 'audio' : 'ocr';
    updateFicheiro(item.id, { status: 'uploading', progresso: 0 });

    try {
      const allMime = [...MIME_OCR, ...MIME_AUDIO];
      const mimeType = allMime.includes(item.file.type)
        ? item.file.type
        : item.tipo === 'audio' ? 'audio/mpeg' : 'application/pdf';

      // PASSO 1: Signed URL
      const step1 = await axios.post(`/api/${endpoint}/signed-url`, {
        fileName:       item.file.name,
        mimeType,
        fileSize:       item.file.size,
        tipo_documento: item.tipo === 'audio' ? 'audio_transcricao' : 'digitalizado_ocr',
        cod_cli:        codCli ? parseInt(codCli) : null,
      });
      const { jobId, signedUrl, gcsKey, fileNameSanitizado } = step1.data.data;

      // PASSO 2: Upload directo para GCS
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', signedUrl);
        xhr.setRequestHeader('Content-Type', mimeType);
        xhr.upload.onprogress = (ev) => {
          if (ev.lengthComputable)
            updateFicheiro(item.id, { progresso: Math.round((ev.loaded / ev.total) * 100) });
        };
        xhr.onload  = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload falhou: ${xhr.status}`)));
        xhr.onerror = () => reject(new Error('Erro de rede no upload'));
        xhr.send(item.file);
      });

      updateFicheiro(item.id, { status: 'processing', progresso: 100 });

      // PASSO 3: Disparar processamento
      await axios.post(`/api/${endpoint}/process`, { jobId, gcsKey, fileNameSanitizado, mimeType });

      // PASSO 4: Polling de estado
      const intervalId = setInterval(async () => {
        try {
          const res = await axios.get(`/api/${endpoint}/status/${jobId}`);
          const { status, documentId } = res.data.data;
          if (status === 'done') {
            clearInterval(intervalId);
            pollingRefs.current.delete(item.id);
            const textoRes = await axios.get(`/api/documentos/${documentId}/texto`);
            const texto = textoRes.data?.data?.texto_integral || '';
            updateFicheiro(item.id, { status: 'done', texto });
          } else if (status === 'error') {
            clearInterval(intervalId);
            pollingRefs.current.delete(item.id);
            updateFicheiro(item.id, { status: 'error', erro: 'Erro no processamento. Tente novamente.' });
          }
        } catch (e) {
          console.error('Polling error:', e);
          clearInterval(intervalId);
          pollingRefs.current.delete(item.id);
          updateFicheiro(item.id, { status: 'error', erro: 'Erro ao obter texto transcrito.' });
        }
      }, POLLING_INTERVAL);
      pollingRefs.current.set(item.id, intervalId);

    } catch (e) {
      console.error('processarAsync error:', e);
      updateFicheiro(item.id, { status: 'error', erro: e.response?.data?.message || e.message });
    }
  }, [codCli, updateFicheiro]);

  const processarPdfLocal = async (item) => {
    updateFicheiro(item.id, { status: 'uploading', progresso: 0 });
    try {
      const formData = new FormData();
      formData.append('file', item.file);
      if (codCli) formData.append('cod_cli', String(codCli));
      formData.append('tipo_documento', 'digitalizado_ocr');

      const res = await axios.post('/api/ocr/extract-local', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (ev) => {
          if (ev.lengthComputable)
            updateFicheiro(item.id, { progresso: Math.round((ev.loaded / ev.total) * 100) });
        },
      });

      if (res.data.success && res.data.data.needsOcr) {
        // PDF com imagens — cair para o pipeline OCR (Document AI)
        updateFicheiro(item.id, { tipo: 'ocr', status: 'pending', progresso: 0 });
        await processarAsync({ ...item, tipo: 'ocr' });
      } else if (res.data.success) {
        updateFicheiro(item.id, { status: 'done', progresso: 100, texto: res.data.data.textoExtraido });
      } else {
        throw new Error(res.data.message || 'Erro desconhecido na extracção');
      }
    } catch (e) {
      // Payload demasiado grande para a serverless function (Vercel devolve 403/413
      // com FUNCTION_PAYLOAD_TOO_LARGE) → redirecionar para pipeline GCS + Document AI
      const httpStatus = e.response?.status;
      const errData    = e.response?.data;
      const isPayloadTooLarge =
        httpStatus === 403 ||
        httpStatus === 413 ||
        String(errData?.error || errData?.message || '').includes('PAYLOAD_TOO_LARGE') ||
        String(e.message || '').toLowerCase().includes('too large');

      if (isPayloadTooLarge) {
        console.warn(`processarPdfLocal: payload demasiado grande (${httpStatus}) — a usar pipeline OCR (Document AI) para ${item.file.name}`);
        updateFicheiro(item.id, { tipo: 'ocr', status: 'pending', progresso: 0 });
        await processarAsync({ ...item, tipo: 'ocr' });
        return;
      }

      console.error('processarPdfLocal error:', e);
      updateFicheiro(item.id, { status: 'error', erro: e.response?.data?.message || e.message });
    }
  };

  // ── Botão único: processar e anexar todos os ficheiros ───────────────────────
  const handleProcessarEAnexar = () => {
    if (!ficheiros.length || processando) return;
    setProcessando(true);
    setErroGlobal('');
    ficheiros.forEach(item => {
      if (item.tipo === 'local')                         processarLocal(item);
      else if (item.tipo === 'pdf-local')                processarPdfLocal(item);
      else if (item.tipo === 'ocr' || item.tipo === 'audio') processarAsync(item);
    });
  };

  // ── Confirmar documentos seleccionados (Meus Documentos) ────────────────────
  const handleConfirmarDocumentos = async () => {
    if (selectedIds.length === 0) {
      setErroDocs('Seleccione pelo menos um documento');
      return;
    }
    setConfirmingDocs(true);
    setErroDocs('');
    try {
      const promises = selectedIds.map(id =>
        axios.get(`/api/attachments/documentos/${id}/texto`)
      );
      const respostas = await Promise.all(promises);
      const anexos = respostas.map(r => ({
        nome:     r.data.data.file_name || 'documento',
        conteudo: r.data.data.texto_integral || '',
      }));
      onAnexar(anexos);
      onClose();
    } catch (e) {
      setErroDocs('Erro ao carregar texto do(s) documento(s): ' + (e.response?.data?.message || e.message));
    } finally {
      setConfirmingDocs(false);
    }
  };

  // ── Helpers de UI ─────────────────────────────────────────────────────────────
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
    setDocumentos([]);
    setSelectedIds([]);
  };

  const handleToggleDoc = (docId) => {
    if (!multiple) {
      setSelectedIds(prev => (prev.includes(docId) ? [] : [docId]));
    } else {
      setSelectedIds(prev =>
        prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]
      );
    }
  };

  const handleLoadMore = () => {
    if (!loadingDocs && hasMore) setPage(prev => prev + 1);
  };

  const formatDate = (str) => {
    if (!str) return '';
    return new Date(str).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  /** Badge de status por ficheiro */
  const renderStatusBadge = (f) => {
    if (f.status === 'pending')    return <span className="aud-badge aud-badge-pending">Pendente</span>;
    if (f.status === 'uploading')  return <span className="aud-badge aud-badge-uploading">A enviar...</span>;
    if (f.status === 'processing') return <span className="aud-badge aud-badge-processing"><span className="aud-spinner" /> A processar...</span>;
    if (f.status === 'done')       return <span className="aud-badge aud-badge-done">Concluído</span>;
    if (f.status === 'error')      return <span className="aud-badge aud-badge-error" title={f.erro}>Erro</span>;
    return null;
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="aud-overlay" onClick={onClose}>
      <div className="aud-dialog" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="aud-header">
          <h3 className="aud-titulo">{titulo || 'Anexar Documento ou Áudio'}</h3>
          <button className="aud-close" onClick={onClose} aria-label="Fechar">✕</button>
        </div>

        {/* Tabs */}
        <div className="aud-tabs">
          <button
            className={`aud-tab ${activeTab === 'ficheiro' ? 'aud-tab-active' : ''}`}
            onClick={() => setActiveTab('ficheiro')}
          >
            📎 Novo Ficheiro
          </button>
          <button
            className={`aud-tab ${activeTab === 'documentos' ? 'aud-tab-active' : ''}`}
            onClick={() => setActiveTab('documentos')}
          >
            📂 Meus Documentos
          </button>
        </div>

        {/* ── Aba: Novo Ficheiro ── */}
        {activeTab === 'ficheiro' && (
          <div className="aud-content">
            <p className="aud-hint">
              Seleccione um ou mais ficheiros. O texto é extraído e usado como contexto.
              PDF com texto extraível é processado localmente; se contiver apenas imagens passa por OCR.
            </p>

            {/* Selecção de ficheiros */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={EXT_TODOS.join(',')}
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              id="aud-file-input"
            />

            <label
              htmlFor="aud-file-input"
              className={`aud-dropzone${processando ? ' aud-dropzone-disabled' : ''}${isDragging ? ' aud-dropzone-dragging' : ''}`}
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <span className="aud-dropzone-icon">📁</span>
              <span className="aud-dropzone-text">Clique para seleccionar ou Arraste ficheiros</span>
              <span className="aud-dropzone-sub">
                PDF · imagens (OCR) · DOCX / TXT · MP3, WAV, FLAC, OGG, WebM · Máx. 500MB por ficheiro
              </span>
            </label>

            {/* Lista de ficheiros */}
            {ficheiros.length > 0 && (
              <div className="aud-file-list">
                {ficheiros.map(f => (
                  <div key={f.id} className={`aud-file-row aud-file-row-${f.status}`}>
                    <span className="aud-file-icon">
                      {f.tipo === 'audio' ? '🎙️' : '📄'}
                    </span>
                    <div className="aud-file-info">
                      <span className="aud-file-name">{f.file.name}</span>
                      <span className="aud-file-size">{formatarTamanho(f.file.size)}</span>
                      {f.status === 'uploading' && (
                        <div className="aud-progress-bar">
                          <div className="aud-progress-fill" style={{ width: `${f.progresso}%` }} />
                        </div>
                      )}
                      {f.erro && <span className="aud-file-erro">{f.erro}</span>}
                    </div>
                    {renderStatusBadge(f)}
                    {!processando && f.status !== 'done' && (
                      <button
                        className="aud-remove-btn"
                        onClick={() => handleRemoverFicheiro(f.id)}
                        title="Remover"
                      >✕</button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Erro global */}
            {erroGlobal && <div className="aud-error">⚠️ {erroGlobal}</div>}

            {/* Acções */}
            <div className="aud-actions">
              {ficheiros.length > 0 && !processando && (
                <button className="aud-btn-primary" onClick={handleProcessarEAnexar}>
                  🚀 Processar e Anexar{ficheiros.length > 1 ? ` (${ficheiros.length})` : ''}
                </button>
              )}
              {processando && (
                <button className="aud-btn-primary" disabled>
                  ⏳ A processar...
                </button>
              )}
              <button className="aud-btn-cancel" onClick={onClose} disabled={processando}>
                Cancelar
              </button>
            </div>
          </div>
        )}

        {/* ── Aba: Meus Documentos ── */}
        {activeTab === 'documentos' && (
          <div className="aud-content">
            <div className="aud-search-row">
              <input
                type="text"
                className="aud-search"
                placeholder="Pesquisar por nome ou tipo..."
                value={search}
                onChange={handleSearchChange}
              />
            </div>

            {selectedIds.length > 0 && (
              <div className="aud-selection-badge">
                ✅ {selectedIds.length} documento(s) seleccionado(s)
              </div>
            )}

            <div className="aud-doc-list">
              {loadingDocs && page === 1 && (
                <div className="aud-loading"><span className="aud-spinner" /> A carregar...</div>
              )}

              {!loadingDocs && documentos.length === 0 && (
                <div className="aud-empty">Nenhum documento encontrado</div>
              )}

              {documentos.map(doc => (
                <label
                  key={doc.id}
                  className={`aud-doc-item ${selectedIds.includes(doc.id) ? 'aud-doc-selected' : ''}`}
                >
                  <input
                    type={multiple ? 'checkbox' : 'radio'}
                    checked={selectedIds.includes(doc.id)}
                    onChange={() => handleToggleDoc(doc.id)}
                  />
                  <div className="aud-doc-info">
                    <div className="aud-doc-nome">📄 {doc.file_name || '(sem nome)'}</div>
                    <div className="aud-doc-meta">
                      <span className="aud-doc-tipo">{doc.tipo_documento}</span>
                      {doc.caso_name && <span className="aud-doc-caso">📁 {doc.caso_name}</span>}
                      <span className="aud-doc-data">{formatDate(doc.created_at)}</span>
                    </div>
                  </div>
                </label>
              ))}

              {hasMore && !loadingDocs && (
                <button className="aud-load-more" onClick={handleLoadMore}>
                  Carregar mais
                </button>
              )}
              {loadingDocs && page > 1 && (
                <div className="aud-loading-more">A carregar...</div>
              )}
            </div>

            {erroDocs && <div className="aud-error">⚠️ {erroDocs}</div>}

            <div className="aud-actions">
              <button
                className="aud-btn-primary"
                onClick={handleConfirmarDocumentos}
                disabled={selectedIds.length === 0 || confirmingDocs}
              >
                {confirmingDocs
                  ? 'A carregar texto...'
                  : `Anexar${selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}`}
              </button>
              <button className="aud-btn-cancel" onClick={onClose} disabled={confirmingDocs}>
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnexarUnificadoDialog;